import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { queryKeys } from 'src/api/db/queryKeys'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useSupabaseClient } from 'src/api/supabase'
import {
  clearStoredGuidedAssistantQuotePublicId,
  getStoredGuidedAssistantQuotePublicId,
  setStoredGuidedAssistantQuotePublicId,
} from 'src/pages/Home/guidedAssistantStore'
import {
  resetRequestChatDraft,
  seedInitialChatNote,
} from 'src/pages/_myAccount/Requests/Request/components/Chat/hooks'
import { SaraAvatar } from 'src/pages/_myAccount/Requests/Request/components/Chat/components/SaraAvatar'
import { SARA_PREFILL_TEXT } from 'src/shared/components/GlobalChatWidget/components/saraPrefill'
import { isDraftQuoteStatus } from 'src/shared/consts/quoteStatus'
import { buildGuidedAssistantPath } from 'src/routes/paths'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { SaraPromptInput } from 'src/shared/components/SaraPromptInput'
import { Badge } from 'src/shared/components/ui/badge'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const toSaraMessage = (text: string) => {
  const trimmed = text.trim()
  if (!trimmed) return ''
  if (/^@?sara\b/i.test(trimmed)) return trimmed
  return `${SARA_PREFILL_TEXT}${trimmed}`
}

const ids = (rows: Array<{ id: number }> | null) =>
  (rows ?? []).map((row) => row.id)

const throwIfError = (error: { message: string } | null) => {
  if (error) throw error
}

/** Wipe a reused guided-assistant draft so the next prompt starts fresh. */
const resetGuidedAssistantSession = async ({
  supabase,
  queryClient,
  quoteId,
}: {
  supabase: ReturnType<typeof useSupabaseClient>
  queryClient: ReturnType<typeof useQueryClient>
  quoteId: number
}) => {
  const { data: chats, error: chatsError } = await supabase
    .from('chat')
    .select('id')
    .eq('quote_id', quoteId)
  throwIfError(chatsError)
  const chatIds = ids(chats)

  const { data: quoteItems, error: itemsError } = await supabase
    .from('quote_item')
    .select('id')
    .eq('quote_id', quoteId)
  throwIfError(itemsError)
  const quoteItemIds = ids(quoteItems)

  let quoteSubItemIds: number[] = []
  if (quoteItemIds.length) {
    throwIfError(
      (
        await supabase
          .from('quote_item')
          .update({ design_product_image: null })
          .in('id', quoteItemIds)
      ).error
    )

    const { data: subItems, error: subError } = await supabase
      .from('quote_sub_item')
      .select('id')
      .in('quote_item_id', quoteItemIds)
    throwIfError(subError)
    quoteSubItemIds = ids(subItems)

    throwIfError(
      (await supabase.from('design').delete().in('quote_item_id', quoteItemIds))
        .error
    )
  }

  if (quoteSubItemIds.length) {
    throwIfError(
      (
        await supabase
          .from('design')
          .delete()
          .in('quote_sub_item_id', quoteSubItemIds)
      ).error
    )
    throwIfError(
      (
        await supabase
          .from('quote_sub_item')
          .delete()
          .in('id', quoteSubItemIds)
      ).error
    )
  }

  if (quoteItemIds.length) {
    throwIfError(
      (await supabase.from('quote_item').delete().eq('quote_id', quoteId)).error
    )
  }

  throwIfError(
    (await supabase.from('quote_proof').delete().eq('quote_id', quoteId)).error
  )

  if (chatIds.length) {
    throwIfError(
      (await supabase.from('message').delete().in('chat_id', chatIds)).error
    )
    throwIfError(
      (await supabase.from('chat').delete().in('id', chatIds)).error
    )
  }

  throwIfError(
    (await supabase.from('order_message').delete().eq('quote_id', quoteId))
      .error
  )

  queryClient.setQueryData([queryKeys.chats, 'byQuote', quoteId], null)
  queryClient.removeQueries({ queryKey: [queryKeys.quoteItems, quoteId] })
  queryClient.removeQueries({ queryKey: [queryKeys.orderMessages] })
  await queryClient.invalidateQueries({
    queryKey: [queryKeys.chats, 'byQuote', quoteId],
  })
}

export const SaraDemoSection = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { t } = useLocalization()
  const org_id = useGetOrgId()
  const { organizationById } = useOrgsApi({ org_id })
  const { quotes, createQuote } = useQuoteApi({ org_id })
  const organization = organizationById?.data
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isPending = isSubmitting || createQuote.isPending

  const handleStartExploring = async (message: string) => {
    const saraMessage = toSaraMessage(message)
    if (!saraMessage || isPending) return

    if (!organization?.id) {
      throw new Error(
        t(
          'Unable to start right now. Please try again.',
          'Kunde inte starta just nu. Försök igen.'
        )
      )
    }

    const openGuidedAssistant = (publicId: string) => {
      seedInitialChatNote(publicId, saraMessage, { replace: true })
      navigate(buildGuidedAssistantPath(publicId), {
        state: {
          initialChatMessage: saraMessage,
          chatResetAt: Date.now(),
        },
      })
    }

    setIsSubmitting(true)
    try {
      const storedPublicId = getStoredGuidedAssistantQuotePublicId(
        organization.id
      )

      if (storedPublicId) {
        const { data: existing, error: existingError } = await supabase
          .from('quote')
          .select('id, status')
          .eq('public_id', storedPublicId)
          .eq('org_id', organization.id)
          .maybeSingle()

        if (existingError) throw existingError

        if (existing && isDraftQuoteStatus(existing.status)) {
          resetRequestChatDraft(storedPublicId)
          await resetGuidedAssistantSession({
            supabase,
            queryClient,
            quoteId: existing.id,
          })
          throwIfError(
            (
              await supabase
                .from('quote')
                .update({ source: 'lead_magnet' })
                .eq('id', existing.id)
            ).error
          )
          openGuidedAssistant(storedPublicId)
          return
        }

        clearStoredGuidedAssistantQuotePublicId(organization.id)
      }

      const nextQuoteNumber = (quotes?.data?.length ?? 0) + 1
      const res = await createQuote.mutateAsync({
        name: `${t('Request', 'Förfrågan')} #${nextQuoteNumber}`,
        org_id: organization.id,
        source: 'lead_magnet',
      })

      if (!res?.public_id) {
        throw new Error(
          t(
            'Unable to start right now. Please try again.',
            'Kunde inte starta just nu. Försök igen.'
          )
        )
      }

      setStoredGuidedAssistantQuotePublicId(organization.id, res.public_id)
      openGuidedAssistant(res.public_id)
    } catch (error) {
      console.error('Error creating request:', error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Column id="try-sara" className="w-full scroll-mt-24">
      <Column className="relative mx-auto w-full min-w-0 overflow-hidden rounded-[32px] border border-stone-300/80 bg-[linear-gradient(165deg,#fbfbfc_0%,#f4f5f7_38%,#eef0f3_72%,#e8eaee_100%)] px-4 py-12 md:rounded-[44px] md:px-8 md:py-24">
        <div
          className="absolute inset-0 [background-image:linear-gradient(to_right,rgba(0,0,0,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.025)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)] [-webkit-mask-image:radial-gradient(circle_at_50%_42%,black,transparent_72%)]"
          aria-hidden
        />

        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden
        >
          <div className="absolute size-[420px] rounded-full border border-stone-200/70 md:size-[560px]" />
          <div className="absolute size-[580px] rounded-full border border-stone-200/50 md:size-[760px]" />
          <div className="absolute size-[740px] rounded-full border border-stone-200/35 md:size-[960px]" />
        </div>

        <Column className="relative z-10 mx-auto w-full max-w-xl items-center gap-8 text-center">
          <Column className="w-full items-center gap-4">
            <Row className="items-center gap-2">
              <SaraAvatar />
              <Badge variant="purple" size="sm" className="font-medium">
                {t('Live', 'Live')}
              </Badge>
            </Row>

            <h2 className="text-balance text-4xl font-normal tracking-tight text-neutral-900 md:text-5xl">
              {t('Try Sara.', 'Prova Sara.')}
            </h2>
            <p className="max-w-xl text-lg leading-relaxed text-neutral-700 md:text-xl">
              {t(
                'Ask her to find products, build kits or make mockups, in the same conversation. This starts a real request. A specialist still reviews the quote.',
                'Be henne hitta produkter, bygga kit eller ta fram mockups, i samma konversation. Det här startar en riktig förfrågan. En specialist granskar ändå offerten.'
              )}
            </p>
          </Column>

          <Column className="w-full items-center gap-2">
            <SaraPromptInput
              onSubmit={handleStartExploring}
              isSubmitting={isPending || !organization?.id}
              showPoweredBy
            />
            <p className="max-w-md text-center text-xs leading-relaxed text-muted-foreground">
              {t(
                'Sara is optional. A Klayder specialist reviews every quote before you approve it.',
                'Sara är valfri. En Klayder-specialist granskar varje offert innan ni godkänner.'
              )}
            </p>
          </Column>
        </Column>
      </Column>
    </Column>
  )
}
